import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Icon from '@welldev-group/rcl-icon';
import Input from '@welldev-group/rcl-input';
import Amount from 'sharedWebpack/Amount';
import classNames from 'classnames';
import debounce from 'throttle-debounce/debounce';
import './style.scss';

const Voucher = ({ voucher, setVoucherInCart }) => {
  const {
    numberFormField,
    valid,
    contentInvalid,
    contentMessage,
    currency,
    reductionAmount,
  } = voucher;

  const [voucherNumber, setVoucherNumber] = useState(numberFormField || '');

  const debouncedSetVoucherInCart = useCallback(
    debounce(600, voucherCode => setVoucherInCart(voucherCode)),
    [setVoucherInCart],
  );

  const initializeVoucher = async () => {
    if (numberFormField) {
      await setVoucherInCart('');
      setVoucherInCart(numberFormField);
    }
  };

  useEffect(() => {
    initializeVoucher();
  }, []);

  const handleVoucherChange = value => {
    setVoucherNumber(value);
    debouncedSetVoucherInCart(value);
  };

  const getErrorMessage = () => {
    if (valid || !contentInvalid) {
      return '';
    }

    return contentMessage;
  };

  const renderDiscountText = () => {
    if (numberFormField && valid) {
      return <Amount currency={currency} value={reductionAmount} />;
    }
    return '0.00';
  };

  const isDiscountAvailable = numberFormField && valid;

  return (
    <div className="voucher d-flex-column flex-gap-12">
      <div className="voucher__header d-flex align-center justify-between">
        <div className="voucher__title d-flex align-center column-gap-8">
          <Icon name="voucher" showBGColor={true} color="success" size="tiny" />
          <span className="voucher__title-text">{I18n.t('ibe.payment.voucher.redeem')}</span>
        </div>
        <div className="voucher__discount d-flex align-center column-gap-8">
          <div>{I18n.t('ibe.payment.voucher.discount')}</div>
          <span
            className={classNames('voucher__discount-amount', {
              'voucher__discount-amount--available': isDiscountAvailable,
            })}
          >
            {renderDiscountText()}
          </span>
        </div>
      </div>
      <div className="voucher__discount-input">
        <Input
          placeholder={I18n.t('ibe.payment.voucher.add_voucher')}
          size="tiny"
          value={voucherNumber}
          onChange={handleVoucherChange}
          isClearable={false}
          touched={!!getErrorMessage()}
          error={getErrorMessage()}
        />
      </div>
    </div>
  );
};

Voucher.propTypes = {
  voucher: PropTypes.shape({
    number: PropTypes.string,
    reductionType: PropTypes.oneOf(['amount', 'percentage']),
    amount: PropTypes.string,
    currency: PropTypes.string,
    percentage: PropTypes.number,
    reductionAmount: PropTypes.string,
    reductionCurrency: PropTypes.string,
    valid: PropTypes.bool,
    numberFormField: PropTypes.string,
    contentInvalid: PropTypes.bool,
    contentMessage: PropTypes.string,
  }).isRequired,
  setVoucherInCart: PropTypes.func.isRequired,
};

export default Voucher;
